'use client';

import { Calendar } from '@workspace/ui/components/calendar';
import { Input } from '@workspace/ui/components/input';
import { Popover, PopoverContent, PopoverTrigger } from '@workspace/ui/components/popover';
import { format } from 'date-fns';
import * as React from 'react';
import { z } from 'zod';

import { cn } from '@workspace/ui/lib';
import { CalendarIcon, X } from 'lucide-react';

export const dateFieldSchema = (errorMessage: string) => z.date({ message: errorMessage });

export function DatePicker({
  value,
  onChange,
  error,
  placeholder,
  disabled,
  className,
  onBlur,
  disabledDates,
  clearable = true,
  readOnly = false,
  defaultMonth,
  onMonthChange,
  'aria-invalid': ariaInvalid,
  popoverContentClassName,
  size = 'base',
}: {
  value: Date | undefined;
  onChange: (date: Date | undefined) => void;
  error?: boolean;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
  onBlur?: () => void;
  disabledDates?: (date: Date) => boolean;
  clearable?: boolean;
  defaultMonth?: Date;
  onMonthChange?: (date: Date) => void;
  'aria-invalid'?: boolean;
  popoverContentClassName?: string;
  size?: 'base' | 'sm';
}) {
  const [open, setOpen] = React.useState(false);

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(undefined);
  };

  return (
    <Popover
      open={open}
      onOpenChange={
        readOnly
          ? undefined
          : (isOpen) => {
              if (!isOpen) onBlur?.();
              if (disabled && isOpen) return;
              setOpen(isOpen);
            }
      }
    >
      <PopoverTrigger asChild disabled={disabled || readOnly}>
        <div
          className={cn(
            'relative w-full outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-0',
            size === 'sm' ? 'rounded-xl' : 'rounded-2xl',
            className
          )}
        >
          <Input
            size={size}
            className={
              readOnly
                ? 'border-grey-300 text-grey-600! dark:border-grey-600 !cursor-default'
                : 'cursor-pointer caret-transparent'
            }
            placeholder={placeholder || 'Select a date'}
            value={value ? format(value, 'dd/MM/yyyy') : ''}
            readOnly={readOnly}
            capitalizeFirstLetter={false}
            endIcon={
              disabled || readOnly ? (
                <CalendarIcon
                  className={cn(disabled ? 'text-grey-300! dark:text-grey-900!' : 'text-grey-600!')}
                />
              ) : value && clearable ? (
                <X
                  className={cn('text-primary cursor-pointer dark:text-neutral-100')}
                  onClick={handleClear}
                />
              ) : (
                <CalendarIcon className={cn('text-primary cursor-pointer dark:text-neutral-100')} />
              )
            }
            onChange={() => {}}
            aria-invalid={error || ariaInvalid ? 'true' : 'false'}
            disabled={disabled}
          />
        </div>
      </PopoverTrigger>
      <PopoverContent
        className={cn(
          'z-[1000] w-[var(--radix-popover-trigger-width)] max-w-[440px] min-w-[300px] p-0 sm:min-w-[356px]',
          popoverContentClassName
        )}
      >
        <Calendar
          mode="single"
          selected={value ?? undefined}
          onSelect={(d) => {
            onChange(d ? (d as Date) : undefined);
            setOpen(false);
          }}
          disabled={disabledDates}
          initialFocus
          defaultMonth={defaultMonth}
          onMonthChange={onMonthChange}
        />
      </PopoverContent>
    </Popover>
  );
}
